import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { useConfigStore } from '../stores/configStore';
import { colors } from '../styles/colors';

/** Compare semver strings. Returns -1, 0, or 1. */
function compareSemver(a: string, b: string): number {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < 3; i++) {
    const va = pa[i] ?? 0;
    const vb = pb[i] ?? 0;
    if (va < vb) return -1;
    if (va > vb) return 1;
  }
  return 0;
}

export default function ForceUpdate() {
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  const config = useConfigStore((s) => s.config);
  const loaded = useConfigStore((s) => s.loaded);
  const [needsUpdate, setNeedsUpdate] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (!loaded || !Capacitor.isNativePlatform()) return;

    const platform = Capacitor.getPlatform() as 'android' | 'ios';
    const minVersion = config.minAppVersion?.[platform];
    if (!minVersion) return;

    // Get current app version from Capgo
    CapacitorUpdater.getDeviceId().catch(() => {}); // warm up plugin
    CapacitorUpdater.current().then((info) => {
      // info.bundle.version is the Capgo bundle version
      // For native version, use App plugin
      import('@capacitor/app').then(({ App }) => {
        App.getInfo().then((appInfo) => {
          const currentVersion = appInfo.version; // e.g. "2.0.0"
          if (compareSemver(currentVersion, minVersion) < 0) {
            setNeedsUpdate(true);
          }
        }).catch(() => {});
      });
    }).catch(() => {
      // Fallback: just check via App plugin
      import('@capacitor/app').then(({ App }) => {
        App.getInfo().then((appInfo) => {
          if (compareSemver(appInfo.version, minVersion) < 0) {
            setNeedsUpdate(true);
          }
        }).catch(() => {});
      });
    });
  }, [loaded, config.minAppVersion]);

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      // Try Capgo live update first
      const bundle = await CapacitorUpdater.download({ url: '', version: 'latest' }).catch(() => null);
      if (bundle?.id) {
        await CapacitorUpdater.set({ id: bundle.id });
        // App will reload automatically
        return;
      }
    } catch {
      // Capgo update not available, redirect to store
    }

    // Fallback: redirect to app store
    const platform = Capacitor.getPlatform();
    const storeUrl = platform === 'ios'
      ? 'https://apps.apple.com/app/ourhertz/id6746971259'
      : 'https://play.google.com/store/apps/details?id=com.ourhertz.ourhertz';
    window.open(storeUrl, '_system');
    setUpdating(false);
  };

  if (!needsUpdate) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.92)' }}>
      <div className="mx-6 max-w-sm rounded-2xl p-6 text-center"
        style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
        <div className="mb-4 text-4xl">🔄</div>
        <h2 className="mb-2 text-lg font-bold" style={{ color: colors.text }}>
          {isKo ? '업데이트가 필요합니다' : 'Update Required'}
        </h2>
        <p className="mb-6 text-sm" style={{ color: colors.textSecondary }}>
          {isKo
            ? '새로운 버전이 출시되었습니다. 앱을 업데이트해주세요.'
            : 'A new version is available. Please update the app to continue.'}
        </p>
        <button
          onClick={handleUpdate}
          disabled={updating}
          className="w-full rounded-xl py-3 text-sm font-bold text-white disabled:opacity-50"
          style={{ backgroundColor: colors.primary }}>
          {updating
            ? (isKo ? '업데이트 중...' : 'Updating...')
            : (isKo ? '업데이트' : 'Update Now')}
        </button>
      </div>
    </div>
  );
}
